import Logo from '@/assets/svg/esig_logo_black.svg';
import styles from '@/pages//styles.less';
import { Auth0Provider, LogoutOptions, useAuth0 } from '@auth0/auth0-react';
import { Spin } from 'antd';
import { ReactNode, createRef, useEffect, useState } from 'react';
import Services from './services';
import Authentication from './services/Authentication';

const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    return (
        <Auth0Provider
            domain="identity.esig.al"
            clientId="u9S8Zl5hJ72yDYwshh66KbiXBEUf7m90"
            audience={'api.esig.al'}
            redirectUri={window.location.origin}
            cacheLocation="localstorage"
            useRefreshTokens
        >
            <ChildrenWrapper>{children}</ChildrenWrapper>
        </Auth0Provider>
    );
};

export default AuthProvider;

export var globalRef: any = null;

export const useLogoutOnStorageChange = (logout: (options?: LogoutOptions) => void) => {
    const logoutFromAllTabs = () => {
        localStorage.setItem('logout', 'true'); // Set the logout

        // Reset the Auth0 session
        logout({ returnTo: window.location.origin });

        // Clear authentication-related data in this tab
        localStorage.removeItem('token');

        localStorage.removeItem('logout');
    };

    useEffect(() => {
        const handleStorageChange = (event) => {
            if (event.key === 'logout' && event.newValue === 'true') {
                logoutFromAllTabs(); // Trigger logout in this tab
            }
        };

        // Listen for changes in localStorage
        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    return logoutFromAllTabs;
};

const ScreenshotWrapper: React.FC<{ children: ReactNode }> = ({ children }) => {
    const ref = createRef<any>();

    useEffect(() => {
        if (ref.current) {
            globalRef = ref.current;
        }
    }, [ref?.current]);

    return (
        <div ref={ref} style={{ height: '100%' }}>
            {children}
        </div>
    );
};

const getGithubActionsCount = async () => {
    const githubActionsRuns = await Services.getGithubActionsRuns();

    const totalCount = githubActionsRuns?.total_count;

    // Ignore invalid responses (e.g. { total_count: null }) to avoid reload loops
    if (typeof totalCount !== 'number' || Number.isNaN(totalCount)) {
        return;
    }

    const storedCount = localStorage.getItem('githubActionsCount');

    if (String(totalCount) !== storedCount) {
        localStorage.setItem('githubActionsCount', String(totalCount));

        window.location.href = window.location.href;
    }
};

const ChildrenWrapper: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { isLoading, loginWithRedirect, user, getAccessTokenSilently, isAuthenticated } = useAuth0();

    const [gettingToken, setGettingToken] = useState<boolean>(true);

    useEffect(() => {
        // Execute the function immediately
        getGithubActionsCount();

        // Set interval to run every 3 hours (3 hours * 60 minutes * 60 seconds * 1000 milliseconds)
        const intervalId = setInterval(getGithubActionsCount, 3 * 60 * 60 * 1000);

        // Clean up the interval on component unmount
        return () => clearInterval(intervalId);
    }, []);

    useEffect(() => {
        (async () => {
            if (!isAuthenticated) return;

            setGettingToken(true);
            const token = await getAccessTokenSilently();

            // Set on local storage since hooks can not be called inside interceptors to authorize headers
            localStorage.setItem('token', token);

            // Reset permissions
            localStorage.removeItem('permissions');

            await Authentication.init();

            const permissions: string[] = Authentication.getPermissions();

            // Set on local storage to use them on access default function
            localStorage.setItem('permissions', JSON.stringify(permissions));

            setGettingToken(false);
        })();
    }, [isAuthenticated]);

    useEffect(() => {
        (async function login() {
            if (!isLoading && !user) {
                await loginWithRedirect({
                    authorizationParams: {
                        redirect_uri: window.location.origin,
                    },
                });
            }
        })();
    }, [isLoading]);

    return (
        <>
            {gettingToken || isLoading ? (
                <div className={styles.pageLoaderContainer}>
                    <Spin size="large" spinning></Spin>
                    <img src={Logo} className={styles.logo} alt="logo" />
                </div>
            ) : (
                <ScreenshotWrapper>{children}</ScreenshotWrapper>
            )}
        </>
    );
};

export const getTokenFromLocalStorage = () => localStorage.getItem('token');
